import { useState, useEffect } from 'react';

interface UseScrollSectionProps {
  sectionIds: string[];
  threshold?: number;
}

export const useScrollSection = ({ sectionIds, threshold = 0.3 }: UseScrollSectionProps) => {
  const [visibleSections, setVisibleSections] = useState<Set<string>>(new Set());

  useEffect(() => {
    const observers = new Map<string, IntersectionObserver>();

    sectionIds.forEach(sectionId => {
      const element = document.getElementById(sectionId);
      if (!element) return;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            setVisibleSections(prev => {
              const newSet = new Set(prev);
              if (entry.isIntersecting) {
                newSet.add(sectionId);
              } else {
                newSet.delete(sectionId);
              }
              return newSet;
            });
          });
        },
        {
          threshold,
          rootMargin: '-10% 0px -10% 0px'
        }
      );

      observer.observe(element);
      observers.set(sectionId, observer);
    });

    return () => {
      observers.forEach(observer => observer.disconnect());
    };
  }, [sectionIds, threshold]);

  return {
    visibleSections,
    isAnySectionVisible: visibleSections.size > 0,
    isSpecificSectionVisible: (sectionId: string) => visibleSections.has(sectionId)
  };
};

export default useScrollSection;
